import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem, Tooltip } from '@qualtrics/ui-react';
import {
  WaffleMenuIcon,
  StarOutlinedIcon,
  StarFilledIcon,
} from '@qualtrics/ui-react/icons';
import styles from './waffle-menu.scss';
import IconButton from '../IconButton/IconButton';
import { WaffleMenuIcon as PortalWaffleMenuIcon } from '../icons';
import { messageService } from '../../Service/message-service';
import {
  getSwitchPortalUrl,
  QUALTRICS_ID,
  switchPortalFromMenu,
  getFavoritePba,
  setFavoritePba,
} from '../../Service/portals-service';
import { getTier1DataTestId } from '../../Service/testing';
import { navigateAfterEnterClick } from '../../Service/navigation-service';
import { useWindowSize } from '../hooks/useWindowSize';
import classNames from 'classnames';
import { useAccountService, usePortalsInfo, useWorkMode } from '../../Context';
import { useScreenType } from '../hooks/useScreenType';
import { useAddClosingFunction } from '../../Context/useAddClosingFunction';

/**
 * @typedef {Object} WaffleMenuProps
 * @property {number} zIndex
 * @property {import(react).Element=} customIcon
 */

/**
 * @type {import(react).FC<WaffleMenu>}
 */
export default function WaffleMenu({ zIndex, customIcon }) {
  const { isMobileView } = useScreenType();
  const [buttonActive, setButtonActive] = React.useState(false);
  const windowSize = useWindowSize();
  const { currentPortalId, currentInstanceId, isPortalsApplication, portals } =
    usePortalsInfo();
  const { debugMode, isFavoritePbaEnabled } = useWorkMode();
  const { accountActionsService } = useAccountService();
  const { addClosingFunction } = useAddClosingFunction();
  const [favoritePba, setFavoritePbaData] = useState(null);
  const favoritePbaRef = useRef(null);
  favoritePbaRef.current = favoritePba;

  useEffect(() => {
    if (isFavoritePbaEnabled) {
      getFavoritePba().then((data) => setFavoritePbaData(data));
    }
  }, [isFavoritePbaEnabled]);

  const waffleButtonAriaLabel = useMemo(
    () =>
      buttonActive
        ? messageService.get('PORTAL_SWITCH_ACCOUNT_MENU')
        : messageService.get('SWITCHER'),
    [buttonActive],
  );

  const filteredAvailablePortals = portals.filter(
    (portal) => portal.id !== QUALTRICS_ID && portal.id !== 'InsightExplorer',
  );

  const bigQualtrics = portals.find((portal) => portal.id === QUALTRICS_ID);

  const shouldDisplayPortals =
    portals &&
    (portals.length > 1 ||
      portals.find(
        (portal) =>
          portal.id === currentPortalId &&
          portal.instanceId === currentInstanceId,
      ) === undefined);

  const handleIsOpenChange = (isOpen) => {
    if (isOpen) {
      accountActionsService.onAppSwitchMenuOpen();
    }
    setButtonActive(isOpen);
  };

  function getPortalButton(portal) {
    if (!portal) {
      return undefined;
    }
    const isActive =
      portal.id === currentPortalId && portal.instanceId === currentInstanceId;
    const isFavorite =
      favoritePba?.id === portal.id &&
      (favoritePba?.instanceId ?? null) === (portal.instanceId ?? null);
    const favoriteButtonLabel = isFavorite
      ? messageService.get('REMOVE_FAVORITE_PBA')
      : messageService.get('ADD_FAVORITE_PBA');
    const menuItemRef = React.createRef();
    const favoriteButtonRef = React.createRef();
    const switchPortalUrl = getSwitchPortalUrl(
      portal,
      currentPortalId,
      portals,
    );

    const handleFavoriteToggle = (toggledPortal) => {
      const current = favoritePbaRef.current;
      const isCurrentlyFavorite =
        current?.id === toggledPortal.id &&
        (current?.instanceId ?? null) === (toggledPortal.instanceId ?? null);
      const newId = isCurrentlyFavorite ? null : toggledPortal.id;
      const newInstanceId = isCurrentlyFavorite
        ? null
        : toggledPortal.instanceId;
      setFavoritePba(newId, newInstanceId).then((result) => {
        if (result.success) {
          setFavoritePbaData(
            newId
              ? { id: toggledPortal.id, instanceId: toggledPortal.instanceId }
              : null,
          );
        }
      });
    };

    return (
      <MenuItem
        onKeyDown={(e) => {
          if (e.key === 'Tab' && !e.shiftKey && isFavoritePbaEnabled) {
            e.preventDefault();
            e.stopPropagation();
            favoriteButtonRef.current?.focus();
          } else {
            switchPortalFromMenu(portal.id, portal.instanceId, currentPortalId);
            navigateAfterEnterClick(e, switchPortalUrl);
          }
        }}
        onSelect={(e) => {
          e.preventDefault();
          switchPortalFromMenu(portal.id, portal.instanceId, currentPortalId);
          if (e.type === 'click') {
            window.location.href = switchPortalUrl;
            return;
          }

          navigateAfterEnterClick(e, switchPortalUrl);
        }}
        aria-label={portal.instanceName || portal.name}
        data-testid={getTier1DataTestId(`Waffle-Menu-PortalItem-${portal.id}`)}
        data-active={isActive || null}
        aria-current={isActive ? true : undefined}
        key={
          portal.instanceId ? `${portal.id}-${portal.instanceId}` : portal.id
        }
        className={classNames(
          isActive
            ? styles.portalContainerActive
            : styles.portalContainerInactive,
          { [styles.portalContainerOnMobile]: isMobileView },
          { [styles.hasFavoriteButton]: isFavoritePbaEnabled },
        )}
        ref={menuItemRef}
      >
        <Tooltip
          lightTheme
          content={portal.instanceName || portal.name}
          delayMs={500}
          zIndex={10002}
          aria-hidden
        >
          <a href={switchPortalUrl} className={styles.portalName}>
            <span className={styles.portalNameLabel}>
              {portal.instanceName || portal.name}
            </span>
          </a>
        </Tooltip>
        {isFavoritePbaEnabled && (
          <IconButton
            tooltipContent={favoriteButtonLabel}
            zIndex={10002}
            aria-label={favoriteButtonLabel}
            aria-pressed={isFavorite}
            data-testid={getTier1DataTestId(
              `Waffle-Menu-FavoriteButton-${portal.instanceId ? `${portal.id}-${portal.instanceId}` : portal.id}`,
            )}
            ref={favoriteButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              handleFavoriteToggle(portal);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleFavoriteToggle(portal);
              } else if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                menuItemRef.current?.focus();
              }
            }}
            className={classNames(styles.favoriteIconButton, {
              [styles.isFavorite]: isFavorite,
            })}
            tabIndex={-1}
          >
            {isFavorite ? <StarFilledIcon /> : <StarOutlinedIcon />}
          </IconButton>
        )}
      </MenuItem>
    );
  }

  useEffect(() => {
    addClosingFunction(() => {
      setButtonActive(false);
    });
  }, []);

  const waffleIconComponent = useMemo(() => {
    const CustomIcon = customIcon;
    if (isPortalsApplication) {
      return CustomIcon ? (
        <CustomIcon
          size="large"
          aria-hidden
          data-testid="Portals-Waffle-Menu"
        />
      ) : (
        <PortalWaffleMenuIcon aria-hidden data-testid="Portals-Waffle-Menu" />
      );
    }
    return <WaffleMenuIcon data-testid="Big-Qualtrics-Waffle-Menu" />;
  }, [customIcon, isPortalsApplication]);

  if (!shouldDisplayPortals) {
    return null;
  }

  return (
    <Menu
      flyoutClassName={classNames(
        isPortalsApplication ? styles.waffleMenuInPortal : styles.waffleMenu,
        { [styles.fullMenuFlyout]: isMobileView },
      )}
      disabled={debugMode}
      onIsOpenChange={handleIsOpenChange}
      isOpen={buttonActive}
      zIndex={zIndex}
      anchor={
        <IconButton
          tooltipContent={messageService.get('SWITCHER')}
          active={buttonActive}
          data-testid={getTier1DataTestId('WaffleMenu')}
          aria-label={waffleButtonAriaLabel}
          zIndex={10002}
        >
          {waffleIconComponent}
        </IconButton>
      }
      maxHeight={windowSize.height - 84}
    >
      {
        <div
          className={
            isPortalsApplication
              ? styles.switchLabel
              : styles.switchLabelInStudio
          }
          data-testid={getTier1DataTestId('Waffle-Menu-PortalSwitcher')}
        >
          {messageService.get('PORTAL_SWITCH_ACCOUNT_MENU')}
        </div>
      }

      {bigQualtrics && getPortalButton(bigQualtrics)}

      {shouldDisplayPortals &&
        filteredAvailablePortals &&
        filteredAvailablePortals.map((portal) => getPortalButton(portal))}
    </Menu>
  );
}

WaffleMenu.propTypes = {
  zIndex: PropTypes.number,
  customIcon: PropTypes.element,
};
